/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import Location from 'modules/Location';
import { getAccessToken } from 'utils/account';
import {
  useGetIsAccountWhitelisted,
  useGetIsAccountWhitelistedElsewhere,
} from 'hooks/use-get-is-account-whitelisted';
import { accountsClient } from 'services/connectivity/accounts/client';
import { Account, AccountRole } from 'services/connectivity/accounts/types';
import { errorDeserialize } from 'utils/error-deserializer';

const CACHE_TIME = 60 * 60 * 1000; // 1h

interface IUseGetMe {
  isAccountWhitelisted: boolean;
  isAccountWhitelistedElsewhere: boolean;
  isAccountVerified: boolean;
  accountId?: string;
  email?: string;
  name?: string;
  avatarUrl?: string;
  roles: AccountRole[];
  isFetching: boolean;
}

export const fetchMe = async (token: string): Promise<Account> => {
  const { result, error } = await accountsClient.getMe(token);

  if (error) {
    throw errorDeserialize(error);
  }

  return result;
};

export const useGetMe = (): IUseGetMe => {
  const token = getAccessToken();
  const emailFromUrl = Location.getUrlParam('email');

  const handleError = (): void => {
    window.location.href = '/';
  };

  const { data, isFetching } = useQuery<Account, string>(['get-me', token], () => fetchMe(token), {
    onError: handleError,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: CACHE_TIME,
  });

  const { isAccountWhitelisted, isFetching: isFetchingWhitelistInfo } =
    useGetIsAccountWhitelisted();

  const { isAccountWhitelistedElsewhere, isFetching: isFetchingWhitelistElsewhereInfo } =
    useGetIsAccountWhitelistedElsewhere();

  return {
    isAccountWhitelisted,
    isAccountWhitelistedElsewhere,
    isAccountVerified: data?.email_verified ?? false,
    accountId: data?.account_id,
    email: emailFromUrl ?? data?.email ?? '',
    name: data?.name ?? '',
    avatarUrl: data?.properties?.avatar_url || '',
    roles: data?.roles ?? [],
    isFetching: isFetching || isFetchingWhitelistInfo || isFetchingWhitelistElsewhereInfo,
  };
};
